import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import AdminDataTable from "./AdminDataTable";
import "./adminServerList.css";

const DEFAULT_LENGTHS = Object.freeze([25, 50, 100]);
const LS_PREFIX = "admin_server_list_state";

function lsKey(pathname) {
  return `${LS_PREFIX}:${pathname || "unknown"}`;
}

function readListState(pathname) {
  try {
    const raw = localStorage.getItem(lsKey(pathname));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeListState(pathname, patch) {
  if (!pathname) return;
  try {
    const prev = readListState(pathname);
    localStorage.setItem(lsKey(pathname), JSON.stringify({ ...prev, ...patch }));
  } catch {
    /* ignore */
  }
}

/**
 * Server-driven list: fetch callback, paging footer, optional sort, optional top search.
 *
 * **Page size next to title:** pass `pageSize` + `onPageSizeChange` and set `showPageSizeInToolbar={false}` (default
 * when both are set). Render {@link ./DataTablePageSize.jsx} in your page header.
 *
 * **Standalone (seller/buyer):** omit `pageSize` / `onPageSizeChange` to keep page size inside the toolbar, or pass
 * `showSearchInput` + `onSearchChange` / internal debounced search.
 *
 * @param {object} props
 * @param {(p: { page: number, pageSize: number, search: string, sortBy?: string, sortDir?: 'asc'|'desc' }) => Promise<{ list: object[], recordsTotal: number, recordsFiltered: number }>} props.fetchPage
 * @param {object[]} props.columnDefs
 * @param {Record<string, boolean>} props.visibility
 * @param {(row: object) => React.ReactNode} [props.renderActions]
 * @param {object[]} [props.prependColumns] See {@link AdminDataTable} — extra columns after Actions.
 * @param {string} [props.search=""]
 * @param {boolean} [props.showSearchInput=true]
 * @param {(value: string) => void} [props.onSearchChange]
 * @param {string} [props.searchPlaceholder]
 * @param {number[]} [props.lengthMenu]
 * @param {number} [props.pageSize] Controlled page size (pair with `onPageSizeChange`).
 * @param {(n: number) => void} [props.onPageSizeChange]
 * @param {boolean} [props.showPageSizeInToolbar] When false, page size is not shown in the list toolbar (use header control). Default: true if uncontrolled, false if controlled.
 * @param {(filtered: number, total: number) => void} [props.onCountsChange]
 * @param {string} [props.defaultSortKey]
 * @param {'asc'|'desc'} [props.defaultSortDir]
 * @param {string} [props.emptyText]
 * @param {(row: object) => string} [props.getRowClassName] Passed to {@link ./AdminDataTable.jsx}.
 * @param {(dragKey: string, dropKey: string) => void} [props.onColumnReorder] Passed to {@link ./AdminDataTable.jsx}.
 */
const AdminServerList = forwardRef(function AdminServerList(
  {
    fetchPage,
    columnDefs,
    visibility,
    renderActions,
    onColumnReorder,
    prependColumns = [],
    search: searchProp = "",
    showSearchInput = true,
    onSearchChange,
    searchPlaceholder = "Search (server-side)…",
    lengthMenu = DEFAULT_LENGTHS,
    pageSize: pageSizeProp,
    onPageSizeChange,
    showPageSizeInToolbar,
    onCountsChange,
    defaultSortKey,
    defaultSortDir = "desc",
    emptyText = "No records.",
    getRowClassName
  },
  ref
) {
  const pagePathname = typeof window !== "undefined" ? window.location.pathname : "";
  const savedState = readListState(pagePathname);
  const controlledPageSize = pageSizeProp != null && typeof onPageSizeChange === "function";
  const [internalPageSize, setInternalPageSize] = useState(() =>
    Number(savedState.pageSize) > 0 ? Number(savedState.pageSize) : lengthMenu[0] ?? 25
  );
  const pageSize = controlledPageSize ? pageSizeProp : internalPageSize;
  const setPageSize = controlledPageSize ? onPageSizeChange : setInternalPageSize;

  const showLengthInToolbar =
    showPageSizeInToolbar !== undefined ? showPageSizeInToolbar : !controlledPageSize;

  const [page, setPage] = useState(() => {
    const p = Number(savedState.page);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [sort, setSort] = useState(() => ({
    key: savedState.sortKey || defaultSortKey || null,
    dir: savedState.sortDir === "asc" ? "asc" : savedState.sortDir === "desc" ? "desc" : defaultSortDir === "asc" ? "asc" : "desc"
  }));
  const [internalRaw, setInternalRaw] = useState(() => String(savedState.searchText ?? ""));
  const [debouncedInternal, setDebouncedInternal] = useState("");
  const reqIdRef = useRef(0);
  const hydratedSearchResetRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedInternal(internalRaw.trim()), 350);
    return () => clearTimeout(t);
  }, [internalRaw]);

  const searchControlled = String(searchProp || "").trim();
  const search =
    showSearchInput && onSearchChange == null ? debouncedInternal : searchControlled;

  useEffect(() => {
    if (!hydratedSearchResetRef.current) {
      hydratedSearchResetRef.current = true;
      return;
    }
    setPage(1);
  }, [search, pageSize]);

  useEffect(() => {
    if (!pagePathname) return;
    writeListState(pagePathname, { page });
  }, [page, pagePathname]);

  useEffect(() => {
    if (!pagePathname || controlledPageSize) return;
    writeListState(pagePathname, { pageSize });
  }, [pageSize, pagePathname, controlledPageSize]);

  useEffect(() => {
    if (!pagePathname) return;
    writeListState(pagePathname, { sortKey: sort.key, sortDir: sort.dir });
  }, [sort.key, sort.dir, pagePathname]);

  const runFetch = useCallback(async () => {
    const id = ++reqIdRef.current;
    setLoading(true);
    try {
      const out = await fetchPage({
        page,
        pageSize,
        search,
        sortBy: sort.key || undefined,
        sortDir: sort.key ? sort.dir : undefined
      });
      if (id !== reqIdRef.current) return;
      const list = Array.isArray(out.list) ? out.list : [];
      const rt = Number(out.recordsTotal) || 0;
      const rf = Number(out.recordsFiltered) >= 0 ? Number(out.recordsFiltered) : rt;
      setRows(list);
      setRecordsTotal(rt);
      setRecordsFiltered(rf);
      onCountsChange?.(rf, rt);
    } catch {
      if (id !== reqIdRef.current) return;
      setRows([]);
      setRecordsTotal(0);
      setRecordsFiltered(0);
      onCountsChange?.(0, 0);
    } finally {
      if (id === reqIdRef.current) setLoading(false);
    }
  }, [fetchPage, page, pageSize, search, sort.key, sort.dir, onCountsChange]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  const reload = useCallback(() => {
    runFetch();
  }, [runFetch]);

  useImperativeHandle(ref, () => ({ reload }), [reload]);

  const total = recordsFiltered;
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize) || 1);

  useEffect(() => {
    setPage((p) => (p > totalPages ? totalPages : p < 1 ? 1 : p));
  }, [totalPages]);

  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = total === 0 ? 0 : Math.min(page * pageSize, total);

  const goFirst = () => setPage(1);
  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const goLast = () => setPage(totalPages);

  const handleSort = useCallback((key) => {
    setPage(1);
    setSort((s) => {
      if (s.key === key) {
        return { key, dir: s.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
  }, []);

  const searchDisplay =
    showSearchInput && onSearchChange == null ? internalRaw : searchProp ?? "";

  useEffect(() => {
    if (!pagePathname || onSearchChange != null) return;
    writeListState(pagePathname, { searchText: internalRaw });
  }, [internalRaw, onSearchChange, pagePathname]);

  const showToolbar = showSearchInput || showLengthInToolbar;
  const toolbarClass = [
    "admin-server-list__toolbar",
    showSearchInput ? "admin-server-list__toolbar--has-search" : "",
    showSearchInput && !showLengthInToolbar ? "admin-server-list__toolbar--search-only" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="admin-server-list">
      {showToolbar && (
        <div className={toolbarClass}>
          {showSearchInput && (
            <input
              type="search"
              className="admin-server-list__search"
              value={searchDisplay}
              onChange={(e) => {
                const v = e.target.value;
                if (onSearchChange) onSearchChange(v);
                else setInternalRaw(v);
              }}
              placeholder={searchPlaceholder}
              aria-label="Search table"
            />
          )}
          {showLengthInToolbar && (
            <label className="admin-server-list__length">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Rows per page"
              >
                {lengthMenu.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>entries</span>
            </label>
          )}
        </div>
      )}

      <AdminDataTable
        columnDefs={columnDefs}
        visibility={visibility}
        rows={rows}
        loading={loading}
        emptyText={emptyText}
        renderActions={renderActions}
        prependColumns={prependColumns}
        onColumnReorder={onColumnReorder}
        sortBy={sort.key}
        sortDir={sort.dir}
        onSortColumn={handleSort}
        wrapperClassName="table-scroll admin-dt-scroll"
        getRowClassName={getRowClassName}
      />

      <div className="admin-server-list__footer">
        <div className="admin-server-list__info">
          {total === 0
            ? "No entries to show"
            : `Showing ${startIdx} to ${endIdx} of ${total} entries`}
          {recordsTotal !== recordsFiltered && recordsTotal > 0
            ? ` (filtered from ${recordsTotal} total)`
            : ""}
        </div>
        <nav className="admin-server-list__paging" aria-label="Pagination">
          <div className="admin-server-list__paging-group" role="group">
            <button type="button" className="admin-server-list__paging-btn" disabled={page <= 1} onClick={goFirst}>
              First
            </button>
            <button type="button" className="admin-server-list__paging-btn" disabled={page <= 1} onClick={goPrev}>
              Previous
            </button>
            <span className="admin-server-list__page-indicator">
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              className="admin-server-list__paging-btn"
              disabled={page >= totalPages}
              onClick={goNext}
            >
              Next
            </button>
            <button
              type="button"
              className="admin-server-list__paging-btn"
              disabled={page >= totalPages}
              onClick={goLast}
            >
              Last
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
});

export default AdminServerList;
